import { fetchRuns } from '@/api/sender';
import type { RunSummary } from '@/entities/sender.entity';
import { useCallback, useEffect, useRef, useState } from 'react';

const TERMINAL_STATUSES = new Set([
	'completed',
	'completed_empty',
	'completed_with_warnings',
	'failed',
	'cancelled',
]);
const POLL_INTERVAL_MS = 4000;
export const SENDER_RUNS_PAGE_SIZE = 25;
const POLL_REFRESH_CAP = 100;

/** Count new runs inserted at the head of `after` (poll prepend). */
export function countPrependedRuns(before: RunSummary[], after: RunSummary[]): number {
	const beforeIds = new Set(before.map(run => run.id));
	let count = 0;
	for (const run of after) {
		if (beforeIds.has(run.id)) break;
		count++;
	}
	return count;
}

/** Fresh list is newest-first; keep paginated tail not present in the refresh window. */
export function mergeRunsById(existing: RunSummary[], fresh: RunSummary[]): RunSummary[] {
	if (fresh.length === 0) return existing;
	const freshIds = new Set(fresh.map(run => run.id));
	const tail = existing.filter(run => !freshIds.has(run.id));
	return [...fresh, ...tail];
}

export function hasActiveSenderRun(runs: RunSummary[]): boolean {
	return runs.some(run => !TERMINAL_STATUSES.has(run.status));
}

export function useSenderRunsFeed() {
	const [runs, setRuns] = useState<RunSummary[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [hasMore, setHasMore] = useState(true);
	const [isFetchingMore, setIsFetchingMore] = useState(false);
	const [loadMoreError, setLoadMoreError] = useState(false);

	const nextCursorRef = useRef<string | null>(null);
	const fetchingRef = useRef(false);
	const runsRef = useRef<RunSummary[]>([]);
	runsRef.current = runs;

	const applyFirstPage = useCallback((items: RunSummary[], nextCursor: string | null | undefined) => {
		setRuns(items);
		nextCursorRef.current = nextCursor ?? null;
		setHasMore(nextCursor != null);
		setError(false);
		setLoadMoreError(false);
	}, []);

	const loadInitial = useCallback(async () => {
		fetchingRef.current = true;
		setLoading(true);
		setError(false);
		try {
			const data = await fetchRuns({ limit: SENDER_RUNS_PAGE_SIZE });
			applyFirstPage(data.items, data.nextCursor);
		} catch {
			if (runsRef.current.length === 0) {
				setError(true);
			}
		} finally {
			setLoading(false);
			fetchingRef.current = false;
		}
	}, [applyFirstPage]);

	const loadMore = useCallback(async () => {
		const cursor = nextCursorRef.current;
		if (fetchingRef.current || !hasMore || cursor == null) return;
		fetchingRef.current = true;
		setIsFetchingMore(true);
		setLoadMoreError(false);
		try {
			const data = await fetchRuns({ limit: SENDER_RUNS_PAGE_SIZE, cursor });
			setRuns(prev => [...prev, ...data.items]);
			nextCursorRef.current = data.nextCursor ?? null;
			setHasMore(data.nextCursor != null);
		} catch {
			setLoadMoreError(true);
		} finally {
			setIsFetchingMore(false);
			fetchingRef.current = false;
		}
	}, [hasMore]);

	const refreshActive = useCallback(async () => {
		if (fetchingRef.current) return;
		const loaded = runsRef.current.length;
		if (loaded === 0) return;
		fetchingRef.current = true;
		try {
			const limit = Math.min(loaded, POLL_REFRESH_CAP);
			const data = await fetchRuns({ limit });
			setRuns(prev => mergeRunsById(prev, data.items));
			if (loaded <= SENDER_RUNS_PAGE_SIZE) {
				nextCursorRef.current = data.nextCursor ?? null;
				setHasMore(data.nextCursor != null);
			}
		} catch {
			// keep stale list on silent poll failure
		} finally {
			fetchingRef.current = false;
		}
	}, []);

	useEffect(() => {
		void loadInitial();
	}, [loadInitial]);

	useEffect(() => {
		if (!hasActiveSenderRun(runs)) return;
		const intervalId = window.setInterval(() => {
			void refreshActive();
		}, POLL_INTERVAL_MS);
		return () => window.clearInterval(intervalId);
	}, [runs, refreshActive]);

	async function retry() {
		await loadInitial();
	}

	return {
		runs,
		loading,
		error,
		hasMore,
		isFetchingMore,
		loadMoreError,
		loadMore,
		retry,
	};
}
